import { useEffect } from 'react';
import { InjectedIntl } from 'react-intl';
import { useStepSubmit } from 'components/Layout/providers/StepSubmitGuardCtx';
import { setJointEligibilityBlocked } from 'services/baoService/actions';
import { getTextFactory } from 'utils/TextUtils';
import { AcmEligibilityResponse } from '../types';
import { EligibilityError, useEligibilityGuard } from './useEligibilityGuard';

type UseJointEligibilityBackBlockParams = {
  intl: InjectedIntl;
  acmEligibilityResponses?: AcmEligibilityResponse[];
  isJointStep: boolean | undefined;
  tooltip?: string;
  dispatch: Function;
};

type UseJointEligibilityBackBlockResult = {
  eligibilityErrors: EligibilityError[];
  hasEligibilityErrors: boolean;
};

/**
 * Blocks the "Back" button (and flags joint eligibility as blocked in redux) while the
 * joint account holder step has unresolved eligibility errors. Clears both on unmount
 * or once the errors are resolved.
 */
export const useJointEligibilityBackBlock = ({
  intl,
  acmEligibilityResponses,
  isJointStep,
  tooltip,
  dispatch,
}: UseJointEligibilityBackBlockParams): UseJointEligibilityBackBlockResult => {
  const getGlobalText = getTextFactory(intl, 'global');
  const getEligibilityText = getTextFactory(intl, 'accountEligibility');
  const { setBackBlocked } = useStepSubmit();

  const { eligibilityErrors } = useEligibilityGuard({ getGlobalText, acmEligibilityResponses });
  const hasEligibilityErrors = Boolean(isJointStep) && eligibilityErrors.length > 0;
  const backBlockedTooltip = tooltip ?? getEligibilityText('backBlockedTooltip');

  useEffect(() => {
    if (!isJointStep) return;

    setBackBlocked(hasEligibilityErrors, hasEligibilityErrors ? backBlockedTooltip : undefined);
    dispatch(setJointEligibilityBlocked(hasEligibilityErrors));

    return () => {
      setBackBlocked(false);
      dispatch(setJointEligibilityBlocked(false));
    };
  }, [isJointStep, hasEligibilityErrors, setBackBlocked, dispatch, backBlockedTooltip]);

  return { eligibilityErrors, hasEligibilityErrors };
};
